
import { useState } from 'react';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image: string;
  tags: string[];
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: "Como Identificar os Sinais de Ansiedade no Dia a Dia",
    excerpt: "A ansiedade é uma resposta natural do corpo, mas quando se torna excessiva pode impactar significativamente nossa qualidade de vida. Aprenda a reconhecer os principais sinais.",
    content: `A ansiedade é uma emoção natural e adaptativa que todos experimentamos em determinados momentos da vida. No entanto, quando se torna excessiva ou desproporcional às situações, pode interferir significativamente em nosso bem-estar e funcionamento diário.

Sinais Físicos da Ansiedade

Os sintomas físicos são frequentemente os primeiros a serem notados:

- **Palpitações ou batimentos cardíacos acelerados**
- **Sudorese excessiva**, especialmente nas mãos
- **Tremores ou sensação de inquietação**
- **Tensão muscular**, principalmente no pescoço e ombros
- **Dificuldades respiratórias** ou sensação de falta de ar
- **Problemas gastrointestinais** como náuseas ou dor de estômago

Sinais Emocionais e Cognitivos

A ansiedade também se manifesta através de:

- **Preocupação excessiva** com situações futuras
- **Pensamentos catastróficos** ou antecipação do pior cenário
- **Dificuldade de concentração** e problemas de memória
- **Irritabilidade** e mudanças bruscas de humor
- **Sensação de perigo iminente** sem causa aparente

Sinais Comportamentais

Observe também mudanças no comportamento:

- **Evitação** de situações que causam desconforto
- **Procrastinação** ou paralisia diante de tarefas
- **Busca constante por reasseguramento** de outras pessoas
- **Alterações no sono** - insônia ou sono excessivo
- **Mudanças no apetite** - comer demais ou perda de apetite

Quando Buscar Ajuda Profissional

É importante procurar ajuda quando:

- Os sintomas persistem por mais de duas semanas
- Interferem nas atividades diárias, trabalho ou relacionamentos
- Causam sofrimento significativo
- Levam ao uso de substâncias para alívio
- Geram pensamentos de autolesão

Estratégias de Manejo Inicial

Enquanto busca ajuda profissional, algumas estratégias podem ajudar:

Técnicas de Respiração
Pratique respiração diafragmática: inspire lentamente pelo nariz por 4 segundos, segure por 4 segundos e expire pela boca por 6 segundos.

Mindfulness e Atenção Plena
Concentre-se no momento presente, observando pensamentos e sensações sem julgamento.

Exercício Físico Regular
A atividade física libera endorfinas e reduz os hormônios do estresse.

Estabeleça Rotinas
Mantenha horários regulares para sono, alimentação e atividades.

A Importância do Acompanhamento Psicológico

A terapia psicanalítica oferece um espaço seguro para compreender as raízes profundas da ansiedade. Através do autoconhecimento e da elaboração de conflitos inconscientes, é possível desenvolver recursos internos mais eficazes para lidar com a ansiedade.

Lembre-se: buscar ajuda é um ato de coragem e autocuidado. A ansiedade tem tratamento e você não precisa enfrentá-la sozinho.`,
    author: "Dra. Cláudia Vianna",
    date: "28 de Dezembro, 2024",
    category: "Ansiedade",
    readTime: "8 min",
    image: "https://readdy.ai/api/search-image?query=peaceful%20woman%20meditating%20in%20serene%20environment%20with%20soft%20natural%20lighting%2C%20mental%20health%20wellness%20concept%2C%20calming%20atmosphere%20with%20plants%20and%20natural%20elements%2C%20professional%20photography%20style&width=800&height=400&seq=anxiety1&orientation=landscape",
    tags: ["Ansiedade", "Sintomas", "Saúde Mental", "Autoconhecimento"]
  },
  {
    id: 2,
    title: "Os Benefícios da Terapia Psicanalítica para o Autoconhecimento",
    excerpt: "Descubra como a psicanálise pode ajudar você a compreender aspectos inconscientes da sua personalidade e promover um crescimento pessoal profundo.",
    content: `A terapia psicanalítica é uma abordagem terapêutica profunda que busca compreender os aspectos inconscientes da mente humana. Desenvolvida por Sigmund Freud e aprimorada por diversos teóricos ao longo dos anos, esta modalidade terapêutica oferece benefícios únicos para quem busca autoconhecimento genuíno.

O Que é a Terapia Psicanalítica?

A psicanálise é um método de investigação da mente que explora:

- **Conteúdos inconscientes** que influenciam nossos comportamentos
- **Padrões repetitivos** em relacionamentos e situações de vida
- **Mecanismos de defesa** que utilizamos para lidar com conflitos
- **Transferência** - como repetimos padrões do passado no presente

Principais Benefícios da Terapia Psicanalítica

1. Autoconhecimento Profundo

A psicanálise permite:
- Compreender motivações inconscientes por trás de comportamentos
- Identificar padrões repetitivos que podem estar sabotando sua vida
- Reconhecer como experiências passadas influenciam o presente
- Desenvolver maior consciência sobre seus desejos e medos

2. Elaboração de Conflitos Internos

Através do processo analítico, você pode:
- Trabalhar traumas e experiências dolorosas do passado
- Resolver conflitos internos que geram ansiedade e sofrimento
- Integrar aspectos rejeitados da personalidade
- Desenvolver uma relação mais harmoniosa consigo mesmo

3. Melhoria nos Relacionamentos

A terapia psicanalítica ajuda a:
- Compreender padrões relacionais disfuncionais
- Desenvolver maior capacidade de intimidade emocional
- Melhorar a comunicação e expressão de sentimentos
- Estabelecer limites saudáveis nos relacionamentos

4. Crescimento Emocional

O processo analítico promove:
- Maior tolerância à frustração e ambiguidade
- Desenvolvimento da capacidade de reflexão
- Ampliação do repertório emocional
- Fortalecimento da resiliência psíquica

Como Funciona o Processo Terapêutico?

A Regra Fundamental
Na psicanálise, o paciente é convidado a falar livremente sobre o que vier à mente, sem censura ou julgamento.

A Interpretação
O analista oferece interpretações que ajudam a conectar conteúdos inconscientes com experiências conscientes.

A Transferência
A relação terapêutica torna-se um laboratório onde padrões relacionais podem ser observados e trabalhados.

O Tempo Terapêutico
A psicanálise é um processo que requer tempo e paciência, pois mudanças profundas acontecem gradualmente.

Quem Pode Se Beneficiar?

A terapia psicanalítica é especialmente indicada para pessoas que:

- Buscam compreensão profunda de si mesmas
- Enfrentam padrões repetitivos de comportamento
- Desejam trabalhar questões relacionais complexas
- Têm interesse em explorar aspectos inconscientes da mente
- Estão dispostas a investir tempo em um processo de autoconhecimento

Diferenças de Outras Abordagens

Enquanto outras terapias podem focar em sintomas específicos ou mudanças comportamentais rápidas, a psicanálise:

- Busca as causas profundas dos conflitos
- Trabalha com a estrutura da personalidade
- Promove mudanças duradouras e significativas
- Desenvolve recursos internos para lidar com futuros desafios

O Papel do Analista

O psicanalista atua como:
- **Facilitador** do processo de autoconhecimento
- **Intérprete** de conteúdos inconscientes
- **Continente** para angústias e conflitos
- **Testemunha** do crescimento pessoal do paciente

Considerações Importantes

É importante entender que:
- A psicanálise requer comprometimento e regularidade
- Os resultados aparecem gradualmente
- Pode haver momentos de desconforto durante o processo
- É um investimento a longo prazo em sua saúde mental

Conclusão

A terapia psicanalítica oferece uma oportunidade única de crescimento pessoal e autoconhecimento. Através da exploração cuidadosa do inconsciente, é possível desenvolver uma compreensão mais profunda de si mesmo e construir uma vida mais autêntica e satisfatória.

Se você sente que há aspectos de sua vida que gostaria de compreender melhor, ou se percebe padrões que se repetem e causam sofrimento, a psicanálise pode ser o caminho para uma transformação genuína e duradoura.`,
    author: "Dra. Cláudia Vianna",
    date: "25 de Dezembro, 2024",
    category: "Psicanálise",
    readTime: "12 min",
    image: "https://readdy.ai/api/search-image?query=thoughtful%20person%20in%20therapy%20session%2C%20comfortable%20office%20setting%20with%20warm%20lighting%2C%20books%20and%20plants%2C%20professional%20therapeutic%20environment%2C%20peaceful%20and%20introspective%20atmosphere&width=800&height=400&seq=psychoanalysis1&orientation=landscape",
    tags: ["Psicanálise", "Autoconhecimento", "Terapia", "Crescimento Pessoal"]
  },
  {
    id: 3,
    title: "Depressão: Compreendendo os Sintomas e Caminhos para o Tratamento",
    excerpt: "A depressão é mais que tristeza passageira. Entenda os sintomas, causas e como a terapia pode ser fundamental no processo de recuperação.",
    content: `A depressão é um transtorno mental que afeta milhões de pessoas em todo o mundo. Caracterizada por uma tristeza profunda e persistente, vai muito além de momentos ocasionais de melancolia que todos experimentamos. É uma condição séria que requer atenção e cuidado profissional.

O Que é Depressão?

A depressão é um transtorno do humor que se manifesta através de:

- **Alterações emocionais** persistentes e significativas
- **Mudanças no funcionamento cognitivo** e comportamental
- **Impacto significativo** na qualidade de vida
- **Duração prolongada** dos sintomas (pelo menos duas semanas)

Principais Sintomas da Depressão

Sintomas Emocionais
- **Tristeza profunda** e persistente
- **Sentimentos de vazio** ou desesperança
- **Perda de interesse** em atividades antes prazerosas
- **Irritabilidade** e mudanças bruscas de humor
- **Sentimentos de culpa** excessiva ou inadequada
- **Baixa autoestima** e autocrítica severa

Sintomas Físicos
- **Alterações no sono** - insônia ou hipersonia
- **Mudanças no apetite** - perda ou aumento significativo
- **Fadiga** e perda de energia
- **Dores físicas** sem causa médica aparente
- **Lentidão psicomotora** ou agitação
- **Problemas de concentração** e memória

Sintomas Comportamentais
- **Isolamento social** e evitação de atividades
- **Negligência** com cuidados pessoais
- **Procrastinação** e dificuldade para tomar decisões
- **Redução da produtividade** no trabalho ou estudos
- **Comportamentos autodestrutivos**

Tipos de Depressão

Episódio Depressivo Maior
Caracterizado por sintomas intensos que duram pelo menos duas semanas e interferem significativamente no funcionamento diário.

Distimia (Transtorno Depressivo Persistente)
Forma mais leve, mas crônica de depressão, com sintomas que persistem por pelo menos dois anos.

Depressão Sazonal
Relacionada a mudanças sazonais, geralmente ocorrendo durante os meses de inverno.

Depressão Pós-Parto
Desenvolve-se após o nascimento de um bebê, afetando a capacidade da mãe de cuidar de si mesma e do filho.

Causas e Fatores de Risco

A depressão resulta de uma combinação complexa de fatores:

Fatores Biológicos
- **Predisposição genética** e histórico familiar
- **Desequilíbrios neuroquímicos** no cérebro
- **Alterações hormonais**
- **Condições médicas** crônicas

Fatores Psicológicos
- **Traumas** e experiências adversas na infância
- **Padrões de pensamento** negativos
- **Baixa autoestima** e autocrítica excessiva
- **Dificuldades** de enfrentamento

Fatores Sociais
- **Estresse** crônico e eventos de vida estressantes
- **Isolamento social** e falta de suporte
- **Problemas financeiros** ou profissionais
- **Perdas** significativas e lutos

O Papel da Terapia Psicanalítica no Tratamento

A abordagem psicanalítica oferece benefícios únicos no tratamento da depressão:

Compreensão Profunda
- Exploração das **raízes inconscientes** da depressão
- Identificação de **padrões relacionais** disfuncionais
- Compreensão de como **experiências passadas** influenciam o presente

Elaboração de Conflitos
- Trabalho com **perdas** não elaboradas
- Processamento de **traumas** e experiências dolorosas
- Resolução de **conflitos internos** que mantêm a depressão

Fortalecimento do Ego
- Desenvolvimento de **recursos internos** para enfrentamento
- Melhoria da **autoestima** e autoimagem
- Ampliação da **capacidade de reflexão** e insight

Estratégias de Enfrentamento

Enquanto busca ajuda profissional, algumas estratégias podem ajudar:

Cuidados Básicos
- Manter **rotina** de sono regular
- Alimentar-se de forma **equilibrada**
- Praticar **exercícios físicos** regulares
- Expor-se à **luz solar** diariamente

Suporte Social
- Manter **contato** com pessoas queridas
- Participar de **grupos de apoio**
- Aceitar **ajuda** quando oferecida
- Evitar **isolamento** excessivo

Atividades Terapêuticas
- Praticar **mindfulness** e meditação
- Engajar-se em **atividades criativas**
- Manter **hobbies** e interesses
- Estabelecer **metas** pequenas e alcançáveis

Quando Buscar Ajuda Profissional

É fundamental procurar ajuda quando:

- Sintomas persistem por mais de duas semanas
- Interferem significativamente na vida diária
- Há pensamentos de autolesão ou suicídio
- Uso de substâncias para lidar com os sintomas
- Perda significativa de funcionamento social ou profissional

Sinais de Alerta para Emergência

Procure ajuda imediata se houver:
- **Pensamentos suicidas** ou planos de autolesão
- **Comportamentos** de risco extremo
- **Psicose** ou perda de contato com a realidade
- **Incapacidade** total de funcionar

O Processo de Recuperação

A recuperação da depressão é possível e envolve:

Tempo e Paciência
- A melhoria é **gradual** e não linear
- Podem haver **recaídas** durante o processo
- É importante manter **esperança** e perseverança

Comprometimento com o Tratamento
- **Regularidade** nas sessões terapêuticas
- **Honestidade** sobre sintomas e sentimentos
- **Participação ativa** no processo terapêutico

Mudanças no Estilo de Vida
- Incorporação de **hábitos saudáveis**
- Desenvolvimento de **estratégias de enfrentamento**
- Fortalecimento de **redes de apoio**

Conclusão

A depressão é uma condição séria, mas tratável. Com o suporte adequado e comprometimento com o tratamento, é possível superar este período difícil e desenvolver recursos para uma vida mais plena e satisfatória.

A terapia psicanalítica oferece um caminho profundo de compreensão e cura, permitindo não apenas o alívio dos sintomas, mas também o crescimento pessoal e o fortalecimento da estrutura psíquica.

Lembre-se: buscar ajuda é um ato de coragem e autocuidado. Você não está sozinho nesta jornada, e a recuperação é possível.`,
    author: "Dra. Cláudia Vianna",
    date: "22 de Dezembro, 2024",
    category: "Depressão",
    readTime: "15 min",
    image: "https://readdy.ai/api/search-image?query=person%20emerging%20from%20darkness%20into%20light%2C%20hope%20and%20healing%20concept%2C%20soft%20warm%20lighting%2C%20therapeutic%20and%20uplifting%20atmosphere%2C%20mental%20health%20recovery%20symbolism&width=800&height=400&seq=depression1&orientation=landscape",
    tags: ["Depressão", "Sintomas", "Tratamento", "Saúde Mental"]
  },
  {
    id: 4,
    title: "TDAH em Adultos: Reconhecendo Sinais e Buscando Tratamento",
    excerpt: "O Transtorno do Déficit de Atenção e Hiperatividade não afeta apenas crianças. Descubra como identificar e tratar o TDAH na vida adulta.",
    content: `O Transtorno do Déficit de Atenção e Hiperatividade (TDAH) é frequentemente associado à infância, mas muitos adultos vivem com esta condição sem diagnóstico. Compreender os sinais e buscar tratamento adequado pode transformar significativamente a qualidade de vida.

O Que é TDAH em Adultos?

O TDAH é um transtorno neurobiológico caracterizado por:

- **Dificuldades de atenção** e concentração
- **Hiperatividade** e inquietação
- **Impulsividade** em decisões e comportamentos
- **Desorganização** e problemas de planejamento

Sintomas de Desatenção em Adultos

No Trabalho
- **Dificuldade** para manter foco em tarefas longas
- **Procrastinação** crônica e perda de prazos
- **Desorganização** de documentos e materiais
- **Esquecimento** de compromissos e responsabilidades
- **Dificuldade** para seguir instruções detalhadas

Na Vida Pessoal
- **Perda frequente** de objetos importantes
- **Dificuldade** para manter rotinas domésticas
- **Problemas** com gestão financeira
- **Esquecimento** de datas importantes
- **Dificuldade** para completar projetos pessoais

Sintomas de Hiperatividade em Adultos

Manifestações Físicas
- **Inquietação** constante e dificuldade para relaxar
- **Necessidade** de estar sempre em movimento
- **Falar excessivamente** ou interromper conversas
- **Dificuldade** para permanecer sentado por longos períodos

Manifestações Mentais
- **Mente acelerada** com muitos pensamentos simultâneos
- **Dificuldade** para "desligar" à noite
- **Sensação** de estar sempre "ligado no 220V"
- **Impaciência** com atividades lentas ou monótonas

Sintomas de Impulsividade

Decisões e Comportamentos
- **Decisões precipitadas** sem considerar consequências
- **Interrupção** frequente de outras pessoas
- **Dificuldade** para esperar sua vez
- **Gastos impulsivos** e problemas financeiros
- **Mudanças frequentes** de emprego ou relacionamentos

Regulação Emocional
- **Explosões** de raiva ou frustração
- **Dificuldade** para controlar emoções intensas
- **Reações exageradas** a situações estressantes
- **Impaciência** com críticas ou feedback

Impactos do TDAH na Vida Adulta

Área Profissional
- **Baixo desempenho** apesar do potencial
- **Conflitos** com colegas e supervisores
- **Dificuldade** para progredir na carreira
- **Mudanças frequentes** de emprego

Relacionamentos
- **Problemas** de comunicação e escuta
- **Conflitos** devido à desorganização
- **Dificuldade** para manter compromissos
- **Sensação** de ser incompreendido

Autoestima
- **Sentimentos** de inadequação e fracasso
- **Autocrítica** severa e culpa
- **Comparação** constante com outros
- **Baixa** confiança em suas capacidades

Diagnóstico em Adultos

O diagnóstico do TDAH em adultos envolve:

Avaliação Clínica Detalhada
- **Histórico** de sintomas desde a infância
- **Avaliação** do funcionamento atual
- **Exclusão** de outras condições médicas
- **Uso** de escalas e questionários específicos

Critérios Diagnósticos
- **Presença** de sintomas antes dos 12 anos
- **Persistência** dos sintomas na vida adulta
- **Prejuízo** significativo em múltiplas áreas
- **Sintomas** não explicados por outras condições

Tratamento Multimodal

Psicoterapia
A terapia psicanalítica pode ajudar com:
- **Compreensão** dos padrões comportamentais
- **Elaboração** de experiências de fracasso
- **Desenvolvimento** de estratégias de enfrentamento
- **Melhoria** da autoestima e autoimagem

Estratégias Comportamentais
- **Organização** e planejamento estruturado
- **Técnicas** de gestão do tempo
- **Sistemas** de lembretes e alertas
- **Divisão** de tarefas grandes em etapas menores

Modificações no Ambiente
- **Redução** de distrações no ambiente de trabalho
- **Criação** de rotinas estruturadas
- **Uso** de ferramentas organizacionais
- **Estabelecimento** de limites claros

Estratégias de Manejo no Dia a Dia

Organização Pessoal
- **Use** agendas e aplicativos de organização
- **Estabeleça** rotinas matinais e noturnas
- **Crie** listas de tarefas e prioridades
- **Mantenha** espaços organizados e limpos

Gestão do Tempo
- **Defina** prazos realistas para tarefas
- **Use** timers para manter foco
- **Planeje** pausas regulares
- **Evite** multitarefas excessivas

Relacionamentos
- **Comunique** suas necessidades e limitações
- **Pratique** escuta ativa
- **Estabeleça** lembretes para datas importantes
- **Seja** paciente consigo mesmo e com outros

Pontos Fortes do TDAH

É importante reconhecer que o TDAH também pode trazer:

Criatividade e Inovação
- **Pensamento** fora da caixa
- **Capacidade** de ver soluções únicas
- **Energia** para projetos criativos
- **Flexibilidade** mental

Energia e Entusiasmo
- **Paixão** intensa por interesses
- **Capacidade** de trabalhar intensamente
- **Entusiasmo** contagiante
- **Resiliência** diante de desafios

Quando Buscar Ajuda

Procure avaliação profissional se:

- Sintomas interferem significativamente na vida diária
- Há histórico de dificuldades desde a infância
- Estratégias de autoajuda não são suficientes
- Existe impacto nos relacionamentos ou trabalho
- Há comorbidades como ansiedade ou depressão

Vivendo Bem com TDAH

Aceitação e Autocompaixão
- **Aceite** suas características únicas
- **Seja** gentil consigo mesmo
- **Celebre** pequenas conquistas
- **Foque** em seus pontos fortes

Suporte Contínuo
- **Mantenha** acompanhamento profissional regular
- **Participe** de grupos de apoio
- **Eduque** familiares e amigos sobre o TDAH
- **Continue** aprendendo sobre a condição

Conclusão

O TDAH em adultos é uma condição real e tratável. Com diagnóstico adequado, tratamento apropriado e estratégias de manejo eficazes, é possível viver uma vida plena e produtiva.

A terapia psicanalítica oferece um espaço valioso para compreender como o TDAH afeta sua vida emocional e desenvolver recursos internos para lidar com os desafios. Lembre-se: ter TDAH não é uma limitação, mas uma diferença que, quando bem compreendida e manejada, pode até mesmo se tornar uma vantagem.

O primeiro passo é buscar ajuda profissional qualificada. Você merece viver com mais organização, foco e bem-estar.`,
    author: "Dra. Cláudia Vianna",
    date: "18 de Dezembro, 2024",
    category: "TDAH",
    readTime: "14 min",
    image: "https://readdy.ai/api/search-image?query=focused%20adult%20working%20with%20organized%20desk%20setup%2C%20productivity%20and%20concentration%20concept%2C%20clean%20modern%20workspace%20with%20natural%20lighting%2C%20mental%20clarity%20and%20organization%20theme&width=800&height=400&seq=adhd1&orientation=landscape",
    tags: ["TDAH", "Adultos", "Concentração", "Tratamento"]
  },
  {
    id: 5,
    title: "Cuidando da Saúde Mental no Trabalho: Estratégias Práticas",
    excerpt: "O ambiente de trabalho pode ser fonte de estresse e ansiedade. Aprenda estratégias eficazes para manter seu bem-estar emocional no ambiente profissional.",
    content: `O ambiente de trabalho moderno apresenta desafios únicos para nossa saúde mental. Entre prazos apertados, pressão por resultados e demandas constantes, é fundamental desenvolver estratégias para manter o equilíbrio emocional e prevenir o burnout.

A Importância da Saúde Mental no Trabalho

Impactos na Produtividade
- **Concentração** e foco melhorados
- **Criatividade** e inovação aumentadas
- **Tomada de decisões** mais eficaz
- **Relacionamentos** interpessoais mais saudáveis

Benefícios Pessoais
- **Satisfação** profissional aumentada
- **Equilíbrio** entre vida pessoal e profissional
- **Autoestima** e confiança fortalecidas
- **Resiliência** diante de desafios

Principais Estressores no Ambiente de Trabalho

Fatores Organizacionais
- **Sobrecarga** de trabalho e prazos irreais
- **Falta** de autonomia e controle
- **Comunicação** deficiente e conflitos
- **Insegurança** no emprego
- **Falta** de reconhecimento e valorização

Fatores Interpessoais
- **Conflitos** com colegas ou supervisores
- **Bullying** ou assédio no trabalho
- **Competição** excessiva entre colegas
- **Isolamento** social no ambiente de trabalho

Fatores Ambientais
- **Ruído** excessivo e distrações
- **Iluminação** inadequada
- **Espaço** físico desconfortável
- **Tecnologia** mal funcionando

Sinais de Alerta para Problemas de Saúde Mental

Sintomas Físicos
- **Fadiga** crônica e exaustão
- **Dores** de cabeça frequentes
- **Tensão** muscular e dores nas costas
- **Problemas** gastrointestinais
- **Alterações** no sono e apetite

Sintomas Emocionais
- **Irritabilidade** e mudanças de humor
- **Ansiedade** constante sobre o trabalho
- **Sentimentos** de desesperança
- **Perda** de motivação e interesse
- **Cynismo** e negatividade

Sintomas Comportamentais
- **Procrastinação** e evitação de tarefas
- **Isolamento** de colegas
- **Aumento** do uso de substâncias
- **Faltas** frequentes ao trabalho
- **Diminuição** da produtividade

Estratégias de Prevenção e Manejo

Gestão do Tempo e Prioridades

Técnica Pomodoro
- **Trabalhe** por 25 minutos focado
- **Faça** uma pausa de 5 minutos
- **Repita** o ciclo 4 vezes
- **Faça** uma pausa longa de 15-30 minutos

Matriz de Eisenhower
Classifique tarefas em:
- **Urgente e Importante** - Faça imediatamente
- **Importante, não Urgente** - Agende
- **Urgente, não Importante** - Delegue
- **Nem Urgente, nem Importante** - Elimine

Técnicas de Relaxamento no Trabalho

Respiração Consciente
- **Inspire** lentamente pelo nariz (4 segundos)
- **Segure** a respiração (4 segundos)
- **Expire** pela boca (6 segundos)
- **Repita** por 2-3 minutos

Relaxamento Muscular Progressivo
- **Tensione** grupos musculares por 5 segundos
- **Relaxe** completamente por 10 segundos
- **Comece** pelos pés e suba até a cabeça
- **Pratique** durante pausas curtas

Estabelecimento de Limites Saudáveis

Limites de Tempo
- **Defina** horários claros de trabalho
- **Evite** verificar e-mails fora do expediente
- **Tire** pausas regulares durante o dia
- **Use** todo seu tempo de férias

Limites de Responsabilidade
- **Aprenda** a dizer "não" quando necessário
- **Delegue** tarefas quando possível
- **Comunique** suas limitações claramente
- **Negocie** prazos realistas

Desenvolvimento de Relacionamentos Positivos

Com Colegas
- **Cultive** amizades genuínas no trabalho
- **Ofereça** apoio e colaboração
- **Participe** de atividades sociais da empresa
- **Pratique** comunicação empática

Com Supervisores
- **Mantenha** comunicação aberta e honesta
- **Solicite** feedback regular
- **Discuta** expectativas e objetivos
- **Busque** oportunidades de desenvolvimento

Criando um Ambiente de Trabalho Saudável

Organização do Espaço
- **Mantenha** sua mesa organizada e limpa
- **Personalize** seu espaço com itens que trazem alegria
- **Garanta** boa iluminação e ventilação
- **Use** plantas para melhorar o ambiente

Pausas Estratégicas
- **Faça** pausas de 5-10 minutos a cada hora
- **Caminhe** durante o almoço
- **Pratique** alongamentos simples
- **Saia** do ambiente de trabalho quando possível

Alimentação e Hidratação
- **Mantenha** uma garrafa de água na mesa
- **Faça** refeições regulares e nutritivas
- **Evite** excesso de cafeína
- **Tenha** lanches saudáveis disponíveis

Lidando com Conflitos e Pressões

Estratégias de Comunicação
- **Use** "eu" em vez de "você" nas conversas
- **Escute** ativamente antes de responder
- **Mantenha** o foco nos fatos, não nas emoções
- **Busque** soluções colaborativas

Manejo da Pressão
- **Identifique** o que está sob seu controle
- **Foque** no processo, não apenas nos resultados
- **Celebre** pequenas conquistas
- **Mantenha** perspectiva sobre a importância relativa das tarefas

Quando Buscar Ajuda Profissional

Sinais de que é Hora de Procurar Apoio
- **Sintomas** persistem por mais de duas semanas
- **Interferência** significativa no desempenho
- **Uso** de substâncias para lidar com o estresse
- **Pensamentos** de autolesão ou desespero
- **Isolamento** social extremo

Recursos Disponíveis
- **Programas** de assistência ao empregado (EAP)
- **Terapia** individual ou em grupo
- **Consultas** médicas para avaliação
- **Grupos** de apoio para profissionais

O Papel da Terapia Psicanalítica

A terapia pode ajudar a:

Compreensão Profunda
- **Identificar** padrões inconscientes que afetam o trabalho
- **Compreender** como experiências passadas influenciam o presente
- **Explorar** conflitos relacionados à autoridade e competição

Desenvolvimento de Recursos
- **Fortalecer** a autoestima e confiança profissional
- **Desenvolver** estratégias de enfrentamento mais eficazes
- **Melhorar** habilidades de relacionamento interpessoal

Construindo Resiliência a Longo Prazo

Autocuidado Contínuo
- **Mantenha** hobbies e interesses fora do trabalho
- **Pratique** exercícios físicos regulares
- **Cultive** relacionamentos pessoais significativos
- **Dedique** tempo para relaxamento e lazer

Desenvolvimento Profissional
- **Invista** em aprendizado contínuo
- **Busque** mentoria e orientação
- **Defina** metas de carreira realistas
- **Mantenha** flexibilidade para mudanças

Mindset de Crescimento
- **Veja** desafios como oportunidades de aprendizado
- **Aceite** que erros fazem parte do processo
- **Foque** no progresso, não na perfeição
- **Mantenha** curiosidade e abertura para novas experiências

Conclusão

Cuidar da saúde mental no trabalho é um investimento essencial em sua qualidade de vida e sucesso profissional. Implementar estratégias preventivas e buscar ajuda quando necessário não é sinal de fraqueza, mas de sabedoria e autocuidado.

Lembre-se de que pequenas mudanças diárias podem ter um impacto significativo a longo prazo. Comece implementando uma ou duas estratégias e gradualmente incorpore outras conforme se sentir confortável.

Sua saúde mental é tão importante quanto sua saúde física. Priorize-a, e você verá melhorias não apenas no trabalho, mas em todas as áreas de sua vida.`,
    author: "Dra. Cláudia Vianna",
    date: "15 de Dezembro, 2024",
    category: "Bem-estar",
    readTime: "16 min",
    image: "https://readdy.ai/api/search-image?query=peaceful%20office%20environment%20with%20natural%20lighting%2C%20organized%20workspace%20with%20plants%2C%20professional%20setting%20promoting%20mental%20wellness%2C%20calm%20and%20productive%20atmosphere&width=800&height=400&seq=workplace1&orientation=landscape",
    tags: ["Trabalho", "Estresse", "Bem-estar", "Produtividade"]
  },
  {
    id: 6,
    title: "Relacionamentos Saudáveis: Como Construir Vínculos Duradouros",
    excerpt: "Descubra os pilares fundamentais para construir relacionamentos saudáveis e duradouros, baseados em comunicação efetiva, respeito mútuo e crescimento conjunto.",
    content: `Os relacionamentos são uma das fontes mais importantes de bem-estar e felicidade em nossas vidas. Seja em relacionamentos amorosos, familiares ou de amizade, a qualidade dos vínculos que estabelecemos impacta diretamente nossa saúde mental e qualidade de vida.

O Que São Relacionamentos Saudáveis?

Relacionamentos saudáveis são caracterizados por:

- **Respeito mútuo** e valorização das diferenças
- **Comunicação aberta** e honesta
- **Confiança** e segurança emocional
- **Apoio** nos momentos difíceis e celebração nas conquistas
- **Crescimento individual** dentro da relação
- **Limites saudáveis** e espaço pessoal

Pilares dos Relacionamentos Saudáveis

1. Comunicação Efetiva

A comunicação é a base de qualquer relacionamento saudável:

Escuta Ativa
- **Preste atenção** total ao que o outro está dizendo
- **Evite** interromper ou formular respostas enquanto o outro fala
- **Demonstre** interesse através de linguagem corporal
- **Faça perguntas** para esclarecer e aprofundar a compreensão

Expressão Honesta
- **Seja claro** sobre seus sentimentos e necessidades
- **Use "eu"** em vez de "você" para evitar acusações
- **Escolha** o momento adequado para conversas importantes
- **Seja específico** sobre situações e comportamentos

2. Confiança e Transparência

A confiança é construída através de:

Consistência
- **Cumpra** promessas e compromissos assumidos
- **Seja** previsível em suas reações e comportamentos
- **Demonstre** confiabilidade através de ações, não apenas palavras

Transparência
- **Compartilhe** pensamentos e sentimentos importantes
- **Seja honesto** sobre erros e limitações
- **Evite** segredos que possam prejudicar a relação

3. Respeito Mútuo

O respeito se manifesta através de:

Valorização das Diferenças
- **Aceite** que o outro pode ter opiniões diferentes
- **Evite** tentar mudar o parceiro/amigo/familiar
- **Celebre** as qualidades únicas de cada pessoa

Limites Saudáveis
- **Respeite** o espaço pessoal e individual
- **Comunique** seus próprios limites claramente
- **Aceite** quando o outro diz "não"

Sinais de Relacionamentos Tóxicos

É importante reconhecer sinais de relacionamentos prejudiciais:

Comportamentos de Alerta
- **Controle excessivo** sobre atividades e relacionamentos
- **Críticas constantes** e desvalorização
- **Manipulação emocional** e chantagem
- **Violência física** ou verbal
- **Isolamento** de amigos e família
- **Ciúme excessivo** e possessividade

Impactos na Saúde Mental
Relacionamentos tóxicos podem causar:
- **Ansiedade** e depressão
- **Baixa autoestima** e autoconfiança
- **Estresse crônico** e problemas físicos
- **Isolamento social** e solidão
- **Dificuldades** em outros relacionamentos

Construindo Relacionamentos Saudáveis

Autoconhecimento

Antes de construir relacionamentos saudáveis com outros, é essencial:

Conhecer a Si Mesmo
- **Identifique** seus valores e prioridades
- **Reconheça** seus padrões relacionais
- **Compreenda** suas necessidades emocionais
- **Trabalhe** questões pessoais não resolvidas

Autoestima Saudável
- **Desenvolva** amor próprio e autoaceitação
- **Estabeleça** limites pessoais claros
- **Cultive** interesses e hobbies próprios
- **Mantenha** independência emocional

Habilidades de Relacionamento

Empatia
- **Coloque-se** no lugar do outro
- **Tente compreender** perspectivas diferentes
- **Valide** sentimentos, mesmo quando não concorda
- **Demonstre** compaixão e compreensão

Resolução de Conflitos
- **Aborde** problemas quando surgem, não os ignore
- **Foque** no problema, não na pessoa
- **Busque** soluções colaborativas
- **Esteja disposto** a ceder e comprometer-se

Relacionamentos Amorosos Saudáveis

Características Específicas

Intimidade Emocional
- **Compartilhe** vulnerabilidades e medos
- **Crie** momentos de conexão profunda
- **Mantenha** romance e afeto
- **Apoie** os sonhos e objetivos do parceiro

Crescimento Conjunto
- **Estabeleça** metas compartilhadas
- **Apoie** o crescimento individual de cada um
- **Enfrente** desafios como equipe
- **Celebre** conquistas juntos

Mantendo a Chama Acesa

Qualidade do Tempo
- **Dedique** tempo exclusivo um ao outro
- **Crie** rituais e tradições especiais
- **Pratique** atividades que ambos gostam
- **Mantenha** conversas significativas

Expressão de Amor
- **Demonstre** afeto de formas variadas
- **Use** palavras de afirmação
- **Ofereça** atos de serviço
- **Dê** presentes significativos
- **Proporcione** toque físico carinhoso

Relacionamentos Familiares

Dinâmicas Familiares Saudáveis

Comunicação Aberta
- **Crie** espaços seguros para diálogo
- **Escute** sem julgamento
- **Respeite** diferentes pontos de vista
- **Resolva** conflitos de forma construtiva

Apoio Mútuo
- **Esteja presente** nos momentos importantes
- **Ofereça** ajuda quando necessário
- **Celebre** conquistas familiares
- **Mantenha** tradições e conexões

Lidando com Famílias Disfuncionais

Estabelecendo Limites
- **Defina** claramente o que é aceitável
- **Comunique** limites de forma respeitosa
- **Mantenha** consistência nas decisões
- **Busque** apoio profissional quando necessário

Amizades Verdadeiras

Qualidades de Bons Amigos

Lealdade e Confiança
- **Mantenha** confidências compartilhadas
- **Seja** confiável em momentos difíceis
- **Apoie** decisões, mesmo quando não concorda totalmente
- **Esteja** presente quando necessário

Reciprocidade
- **Equilibre** dar e receber na amizade
- **Invista** tempo e energia na relação
- **Demonstre** interesse genuíno na vida do amigo
- **Seja** generoso com tempo e atenção

O Papel da Terapia nos Relacionamentos

Quando Buscar Ajuda

A terapia pode ser útil quando:
- **Padrões** relacionais prejudiciais se repetem
- **Conflitos** não conseguem ser resolvidos
- **Comunicação** se torna difícil ou impossível
- **Traumas** passados afetam relacionamentos atuais

Benefícios da Terapia de Casal/Família

Melhoria da Comunicação
- **Aprenda** técnicas de comunicação efetiva
- **Desenvolva** habilidades de escuta
- **Pratique** expressão de sentimentos
- **Resolva** mal-entendidos

Fortalecimento do Vínculo
- **Redescubra** qualidades positivas um do outro
- **Crie** novas formas de conexão
- **Trabalhe** objetivos compartilhados
- **Renove** compromissos mútuos

Mantendo Relacionamentos a Longo Prazo

Investimento Contínuo

Atenção e Cuidado
- **Demonstre** interesse genuíno regularmente
- **Lembre-se** de datas e eventos importantes
- **Faça** pequenos gestos de carinho
- **Mantenha** contato regular

Adaptação e Crescimento
- **Aceite** que pessoas mudam com o tempo
- **Adapte-se** a novas fases da vida
- **Cresça** junto com mudanças
- **Mantenha** flexibilidade e abertura

Superando Desafios

Perdão e Reconciliação
- **Pratique** o perdão genuíno
- **Aprenda** com erros e conflitos
- **Foque** no futuro, não apenas no passado
- **Trabalhe** juntos para reconstruir confiança

Conclusão

Relacionamentos saudáveis são uma das maiores fontes de felicidade e bem-estar em nossas vidas. Eles requerem investimento, paciência e habilidades que podem ser desenvolvidas ao longo do tempo.

A terapia psicanalítica pode oferecer insights valiosos sobre padrões relacionais inconscientes e ajudar a desenvolver relacionamentos mais autênticos e satisfatórios. Através do autoconhecimento e do desenvolvimento de habilidades relacionais, é possível construir vínculos duradouros e significativos.

Lembre-se: relacionamentos saudáveis não são perfeitos, mas são baseados em respeito mútuo, comunicação aberta e crescimento conjunto. Investir em seus relacionamentos é investir em sua própria felicidade e bem-estar.`,
    author: "Dra. Cláudia Vianna",
    date: "30 de Dezembro, 2024",
    category: "Relacionamentos",
    readTime: "18 min",
    image: "https://readdy.ai/api/search-image?query=happy%20couple%20or%20friends%20having%20meaningful%20conversation%20in%20comfortable%20setting%2C%20warm%20lighting%2C%20genuine%20connection%20and%20communication%2C%20healthy%20relationship%20dynamics%2C%20peaceful%20and%20supportive%20atmosphere&width=800&height=400&seq=relationships1&orientation=landscape",
    tags: ["Relacionamentos", "Comunicação", "Amor", "Família", "Amizade"]
  }
];

// ... existing code ...

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState<string>('Todos');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const categories = ['Todos', 'Ansiedade', 'Psicanálise', 'Depressão', 'TDAH', 'Bem-estar', 'Relacionamentos'];

  const filteredPosts = selectedCategory === 'Todos' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-xl"></i>
              </div>
              <h1
                className="text-2xl font-bold text-emerald-600"
                style={{ fontFamily: "Allura, cursive" }}
              >
                Psicóloga Cláudia Vianna
              </h1>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
                Início
              </a>
              <a href="/blog" className="text-emerald-600 font-semibold cursor-pointer">
                Blog
              </a>
              <a href="/#sobre" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
                Sobre
              </a>
              <a href="/#servicos" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
                Serviços
              </a>
              <a href="/#contato" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
                Contato
              </a>
              <a
                href="https://wa.me/5521999351239?text=Olá!%20Gostaria%20de%20agendar%20uma%20consulta%20com%20a%20Psicóloga%20Cláudia%20Vianna."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-emerald-600 text-white px-6 py-2 rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Agendar Consulta
              </a>
            </nav>
            <button className="md:hidden text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </header>

        {/* Article Content */}
        <article className="pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Back Button */}
            <button
              onClick={() => setSelectedPost(null)}
              className="flex items-center space-x-2 text-emerald-600 hover:text-emerald-7 transition-colors mb-8 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Voltar ao Blog</span>
            </button>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center space-x-4 mb-4">
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                  {selectedPost.category}
                </span>
                <span className="text-gray-500 text-sm">{selectedPost.readTime} de leitura</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-tight">
                {selectedPost.title}
              </h1>
              <div className="flex items-center space-x-4 text-gray-600">
                <span>Por {selectedPost.author}</span>
                <span>•</span>
                <span>{selectedPost.date}</span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-8">
              <img
                src={selectedPost.image}
                alt={selectedPost.title}
                className="w-full h-96 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-xl text-gray-600 mb-8 leading-relaxed font-medium">
                {selectedPost.excerpt}
              </div>
              <div 
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: selectedPost.content.replace(/\n/g, '<br>').replace(/## (.*?)<br>/g, '<h2 class="text-2xl font-bold text-gray-800 mt-8 mb-4">$1</h2>').replace(/### (.*?)<br>/g, '<h3 class="text-xl font-semibold text-gray-800 mt-6 mb-3">$1</h3>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/- \*\*(.*?)\*\*/g, '<li class="mb-2"><strong>$1</strong></li>').replace(/- (.*?)<br>/g, '<li class="mb-1">$1</li>')
                }}
              />
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {selectedPost.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-emerald-50 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Precisa de Ajuda Profissional?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Se você se identificou com este artigo e gostaria de conversar sobre suas questões pessoais, 
                estou aqui para ajudar. Agende uma consulta e vamos trabalhar juntos em seu bem-estar.
              </p>
              <a
                href="https://wa.me/5521999351239?text=Olá!%20Gostaria%20de%20agendar%20uma%20consulta%20com%20a%20Psicóloga%20Cláudia%20Vianna."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-emerald-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Agendar Consulta
              </a>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <h1
              className="text-2xl font-bold text-emerald-600"
              style={{ fontFamily: "Allura, cursive" }}
            >
              Psicóloga Cláudia Vianna
            </h1>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Início
            </a>
            <a href="/blog" className="text-emerald-600 font-semibold cursor-pointer">
              Blog
            </a>
            <a href="/#sobre" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Sobre
            </a>
            <a href="/#servicos" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Serviços
            </a>
            <a href="/#contato" className="text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">
              Contato
            </a>
            <a
              href="https://wa.me/5521999351239?text=Olá!%20Gostaria%20de%20agendar%20uma%20consulta%20com%20a%20Psicóloga%20Cláudia%20Vianna."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-600 text-white px-6 py-2 rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Agendar Consulta
            </a>
          </nav>
          <button className="md:hidden text-gray-700 cursor-pointer">
            <i className="ri-menu-line text-2xl"></i>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-emerald-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
              Blog de <span className="text-emerald-600">Saúde Mental</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Artigos e insights sobre psicologia, bem-estar emocional e desenvolvimento pessoal. 
              Conteúdo especializado para ajudar você em sua jornada de autoconhecimento.
            </p>
            <div className="flex items-center justify-center space-x-4 text-gray-500">
              <div className="flex items-center space-x-2">
                <i className="ri-article-line text-emerald-600"></i>
                <span>{blogPosts.length} artigos</span>
              </div>
              <span>•</span>
              <div className="flex items-center space-x-2">
                <i className="ri-user-line text-emerald-600"></i>
                <span>Por Dra. Cláudia Vianna</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                <div className="aspect-video">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                    <span className="text-gray-500 text-sm">{post.readTime}</span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>{post.author}</span>
                      <span>•</span>
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-emerald-600">
                      <span className="text-sm font-medium">Ler mais</span>
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-emerald-800">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Receba Novos Artigos por Email
          </h3>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Inscreva-se em nossa newsletter e receba conteúdos exclusivos sobre saúde mental e bem-estar diretamente em seu email.
          </p>
          <div className="max-w-md mx-auto flex gap-4">
            <input
              type="email"
              placeholder="Seu melhor email"
              className="flex-1 px-4 py-3 rounded-full border-0 focus:ring-2 focus:ring-emerald-300 outline-none"
            />
            <button className="bg-white text-emerald-800 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Inscrever-se
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">
            Precisa de Acompanhamento Profissional?
          </h3>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Se você se identificou com algum dos temas abordados em nossos artigos, 
            agende uma consulta para conversar sobre suas necessidades específicas.
          </p>
          <a
            href="https://wa.me/5521999351239?text=Olá!%20Gostaria%20de%20agendar%20uma%20consulta%20com%20a%20Psicóloga%20Cláudia%20Vianna."
            target="_blank"
            rel="noopener noreferrer"
            className="bg-emerald-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            Agendar Consulta
          </a>
        </div>
      </section>

     {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-white text-xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-emerald-400">Psicóloga Cláudia Vianna</h4>
              </div>
              <p className="text-gray-300 mb-6">
                Atendimento psicológico com carinho, respeito e profissionalismo para promover seu bem-estar emocional.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://instagram.com/psi.claudiavianna"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-white"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-white"></i>
                </a>
               <a
                  href="https://www.youtube.com/@PsicólogaCláudiaVianna"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer"
                >
                   <i className="ri-youtube-fill text-white"></i>
                </a>
              </div>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-6">Links Rápidos</h5>
              <ul className="space-y-3">
                <li><a href="#inicio" className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer">Início</a></li>
                <li><a href="#sobre" className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer">Sobre</a></li>
                <li><a href="#servicos" className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer">Serviços</a></li>
                <li><a href="/blog" className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer">Blog</a></li>
                <li><a href="#contato" className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer">Contato</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-semibold mb-6">Informações de Contato</h5>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-emerald-400 mr-3 mt-1"></i>
                <div className="text-gray-300">
                  <p>Avenida Benvindo de Novaes, 1900 -Recreio dos Bandeirantes, Rio de Janeiro – RJ, 22790‑382</p>
                  <p>Sala 204</p>
                </div>
              </div>
                <div className="flex items-center">
                  <i className="ri-whatsapp-line text-emerald-400 mr-3"></i>
                  <span className="text-gray-300">(21) 99935-1239</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-line text-emerald-400 mr-3"></i>
                  <span className="text-gray-300">psiclaudiavianna@gmail.com</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Psicóloga Cláudia Vianna. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
